// Reusable skeleton loading states for different content types
// Bloomberg Terminal style: Professional, consistent loading UX

import { Skeleton } from './skeleton';
import { Card } from './card';

/**
 * Skeleton for metric tiles (dashboard KPIs)
 */
export function MetricTileSkeleton() {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </Card>
  );
}

/**
 * Skeleton for project cards
 */
export function ProjectCardSkeleton() {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
          <Skeleton className="h-8 w-8 rounded" />
        </div>
        <div className="flex items-center gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </Card>
  );
}

/**
 * Skeleton for table rows (records, analyses, users)
 */
export function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 border-b border-border py-4">
      <Skeleton className="h-10 w-10 rounded" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-8 w-20" />
    </div>
  );
}

/**
 * Skeleton for full table
 */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: rows }).map((_, i) => (
            <TableRowSkeleton key={i} />
          ))}
        </div>
      </div>
    </Card>
  );
}

/**
 * Skeleton for charts
 */
export function ChartSkeleton() {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-2">
          <div className="flex items-end gap-2 h-[200px]">
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton
                key={i}
                className="flex-1"
                style={{ height: `${50 + Math.random() * 50}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Skeleton for dashboard (multiple metrics + charts)
 */
export function DashboardSkeleton({
  'data-testid': testId = 'dashboard-skeleton',
}: {
  /**
   * Шесть вызывающих мест передавали сюда свой testid — по одному на роль —
   * а компонент не принимал пропсов вовсе и рисовал литерал. Все шесть молча
   * терялись, и загрузочные состояния четырёх ролевых дашбордов были
   * неотличимы друг от друга.
   */
  'data-testid'?: string;
} = {}) {
  return (
    <div className="grid gap-6 p-6" data-testid={testId}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricTileSkeleton />
        <MetricTileSkeleton />
        <MetricTileSkeleton />
        <MetricTileSkeleton />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <ChartSkeleton />
        <ChartSkeleton />
      </div>
    </div>
  );
}

/**
 * Skeleton for projects grid
 */
export function ProjectsGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      data-testid="projects-skeleton"
    >
      {Array.from({ length: count }).map((_, i) => (
        <ProjectCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Skeleton for list view (generic)
 */
export function ListSkeleton({ items = 8 }: { items?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded border border-border">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for full app layout (Sidebar + Topbar + Main)
 * Used during initial auth loading
 */
export function AppLayoutSkeleton() {
  return (
    <div className="flex h-screen bg-bg">
      {/* Sidebar Skeleton */}
      <div className="hidden md:flex w-64 flex-col border-r border-border bg-bg-elevated p-4">
        {/* Logo */}
        <div className="mb-8">
          <Skeleton className="h-8 w-32" />
        </div>

        {/* Navigation Items */}
        <div className="flex-1 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>

        {/* User Profile at bottom */}
        <div className="mt-4 flex items-center gap-3 p-3 rounded border border-border">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col">
        {/* Topbar Skeleton */}
        <div className="flex items-center justify-between border-b border-border bg-bg-elevated px-6 py-4">
          <Skeleton className="h-6 w-48" />
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-full" />
            <Skeleton className="h-9 w-9 rounded-full" />
            <Skeleton className="h-9 w-9 rounded-full" />
          </div>
        </div>

        {/* Main Dashboard Content */}
        <main className="flex-1 overflow-auto p-6">
          <DashboardSkeleton />
        </main>
      </div>
    </div>
  );
}
